/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : mainframeTheme.ts
// 설명 : 메인프레임 화면 테마(버전) CSS 로더
//  - DB(ZA_THEMA_MAINFRAME)에는 "어떤 버전이 적용중인가(CSS_KEY)"만 저장하고, CSS_KEY -> 실제 CSS 파일 묶음의 연결은
//    아래 MAINFRAME_THEME_SETS(코드)가 담당함. 새 버전을 추가하려면 CSS 세트 파일(mainframeThemeXX.ts) + 이 표에 한 줄 + DB 행이 필요함.
//  - 화면별 CSS import는 두지 않음 - 앱 시작(index.tsx)에서 적용중 버전의 CSS 묶음을 한 번만 로드한 뒤 화면을 그림.
//  - 적용중 버전 값(CSS_KEY)은 로그인 시 서버에서 받아 localStorage에 저장해 두고 여기서 읽음(없으면 기본값).
//  - CSS는 한 번 로드되면 되돌릴 수 없으므로 버전이 바뀌면 화면을 새로 고쳐야(다시 로그인/새로고침) 적용됨.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import axios from 'axios';
import { API_BASE_URL } from '../config'; // API 기본 URL

export const MAINFRAME_THEME_STORAGE_KEY = 'mainframeThemeKey';
export const MAINFRAME_THEME_DEFAULT = 'v1';

// CSS_KEY(소문자) -> CSS 세트 로더
const MAINFRAME_THEME_SETS: Record<string, () => Promise<unknown>> = {
  v0: () => import('./mainframeThemeV0'),
  v1: () => import('./mainframeThemeV1'),
};

/** 저장된 적용중 버전 키(등록되지 않은 값이면 기본값) */
export const getMainframeThemeKey = (): string => {
  try {
    const saved = (localStorage.getItem(MAINFRAME_THEME_STORAGE_KEY) || '').toLowerCase();
    if (saved && MAINFRAME_THEME_SETS[saved]) return saved;
  } catch { /* 저장소 접근 불가 시 기본값 */ }
  return MAINFRAME_THEME_DEFAULT;
};

/** 적용중 버전 키를 저장 - 다음 앱 시작(새로고침)부터 반영됨. 코드에 등록되지 않은 키(DB에만 있는 버전)는 저장하지 않고 기본값으로 되돌림 */
export const setMainframeThemeKey = (key: string | null | undefined) => {
  try {
    const k = String(key || '').toLowerCase();
    if (k && MAINFRAME_THEME_SETS[k]) localStorage.setItem(MAINFRAME_THEME_STORAGE_KEY, k);
    else {
      if (k) console.warn('코드에 등록되지 않은 메인프레임 테마(CSS_KEY):', k);
      localStorage.removeItem(MAINFRAME_THEME_STORAGE_KEY);
    }
  } catch { /* ignore */ }
};

let loadedKey = MAINFRAME_THEME_DEFAULT; // 이번 앱 시작에서 실제로 로드한 버전

/** 적용중 버전의 CSS 묶음 로드 - 실패해도 화면은 떠야 하므로 기본 버전으로 한 번 더 시도 */
export const loadMainframeTheme = async (): Promise<void> => {
  const key = getMainframeThemeKey();
  try {
    await MAINFRAME_THEME_SETS[key]();
    loadedKey = key;
  } catch (e) {
    console.error('메인프레임 테마 로드 실패:', key, e);
    if (key !== MAINFRAME_THEME_DEFAULT) {
      try { await MAINFRAME_THEME_SETS[MAINFRAME_THEME_DEFAULT](); loadedKey = MAINFRAME_THEME_DEFAULT; } catch { /* ignore */ }
    }
  }
};

/**
 * 로그인 직후 호출 - 서버에서 적용중 버전(예약적용일시가 지난 예약은 서버가 먼저 승격)을 받아 저장함.
 * 반환값 true = 지금 로드된 CSS와 다른 버전이라 화면을 새로 불러와야 함(CSS는 한 번 로드되면 되돌릴 수 없음).
 * 서버 오류/미응답이면 저장된 값을 그대로 두고 false(로그인은 막지 않음).
 */
export const syncMainframeTheme = async (langCd: string): Promise<boolean> => {
  try {
    const res = await axios.post(`${API_BASE_URL}/com/apiMainframeTheme`, { langCd });
    const row = res.data?.rsResults;
    if (row?.css_key) setMainframeThemeKey(row.css_key);
  } catch { /* 저장된 값 유지 */ }
  return getMainframeThemeKey() !== loadedKey;
};
